import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import '../css/chapters.css';

// New Chapter page (creates a new `schools/{schoolId}` document)
// NOTE: Chapters live in the `schools` collection per your Firestore structure.

function slugifySchoolId(input) {
    return (input || '')
        .trim()
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9-]/g, '')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '');
}

const DEFAULT_FORM = {
    schoolId: '',
    schoolName: '',
    city: '',
    state: '',
    contactEmail: '',
    active: true,
    // Common chapter links
    groupMe: '',
    instagram: '',
    subscribeUrl: '',
};

export default function NewChapter() {
    const [form, setForm] = useState(DEFAULT_FORM);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const navigate = useNavigate();

    const derivedId = useMemo(() => slugifySchoolId(form.schoolId || form.schoolName), [form.schoolId, form.schoolName]);

    function updateField(key, value) {
        setForm((prev) => ({ ...prev, [key]: value }));
    }

    async function handleCreate(e) {
        e.preventDefault();
        setError('');
        setSuccess('');

        const schoolId = derivedId;
        if (!schoolId) {
            setError('Please enter a School ID or School Name.');
            return;
        }
        if (!form.schoolName.trim()) {
            setError('Please enter a School Name.');
            return;
        }

        try {
            setIsSaving(true);

            // Minimal but useful schema; safe defaults.
            // You can add/rename fields later without breaking existing docs.
            await setDoc(
                doc(db, 'schools', schoolId),
                {
                    schoolId,
                    schoolName: form.schoolName.trim(),
                    city: form.city.trim(),
                    state: form.state.trim(),
                    contactEmail: form.contactEmail.trim(),
                    active: !!form.active,

                    // Optional marketing/CTA fields
                    ctaGroupMe: form.groupMe.trim(),
                    ctaInstagram: form.instagram.trim(),
                    ctaEmail: form.subscribeUrl.trim(),

                    // Timestamps
                    createdAt: serverTimestamp(),
                    updatedAt: serverTimestamp(),
                },
                { merge: true }
            );
            setSuccess(`Created/updated chapter: ${schoolId}`);
            // Send the user straight to the editor for this chapter
            navigate(`/edit-chapter/${schoolId}`);
        } catch (err) {
            console.error('Create chapter failed:', err);
            setError(err?.message || 'Failed to create chapter.');
        } finally {
            setIsSaving(false);
        }
    }

    return (
        <div className="newchapter-page">
            <div className="newchapter-head">
                <div className="newchapter-headText">
                    <h1 className="newchapter-title">New Chapter</h1>
                    <p className="newchapter-subtitle">
                        Create a new school (chapter) in Firestore under <code>schools/&lt;schoolId&gt;</code>.
                    </p>
                </div>
            </div>

            <div className="newchapter-card">
                <form onSubmit={handleCreate} className="newchapter-form">
                    <div className="newchapter-grid">
                        <div className="newchapter-field">
                            <label className="newchapter-label">School ID (slug)</label>
                            <input
                                className="newchapter-input"
                                value={form.schoolId}
                                onChange={(e) => updateField('schoolId', e.target.value)}
                                placeholder="e.g., tamu"
                            />
                            <div className="newchapter-help">
                                Will save to: <code>schools/{derivedId || '...'}</code>
                            </div>
                        </div>

                        <div className="newchapter-field">
                            <label className="newchapter-label">School Name</label>
                            <input
                                className="newchapter-input"
                                value={form.schoolName}
                                onChange={(e) => updateField('displayName', e.target.value)}
                                placeholder="e.g., Texas A&M University"
                                required
                            />
                        </div>

                        <div className="newchapter-field">
                            <label className="newchapter-label">City</label>
                            <input
                                className="newchapter-input"
                                value={form.city}
                                onChange={(e) => updateField('city', e.target.value)}
                                placeholder="College Station"
                            />
                        </div>

                        <div className="newchapter-field">
                            <label className="newchapter-label">State</label>
                            <input
                                className="newchapter-input"
                                value={form.state}
                                onChange={(e) => updateField('state', e.target.value)}
                                placeholder="TX"
                            />
                        </div>

                        <div className="newchapter-field">
                            <label className="newchapter-label">Contact Email</label>
                            <input
                                className="newchapter-input"
                                value={form.contactEmail}
                                onChange={(e) => updateField('contactEmail', e.target.value)}
                                placeholder="chapter@school.edu"
                                type="email"
                            />
                        </div>

                        <div className="newchapter-field newchapter-checkboxRow">
                            <input
                                id="active"
                                type="checkbox"
                                checked={!!form.active}
                                onChange={(e) => updateField('active', e.target.checked)}
                            />
                            <label htmlFor="active" className="newchapter-label newchapter-checkboxLabel">Active</label>
                        </div>

                        <div className="newchapter-field">
                            <label className="newchapter-label">GroupMe Link</label>
                            <input
                                className="newchapter-input"
                                value={form.groupMe}
                                onChange={(e) => updateField('groupMe', e.target.value)}
                                placeholder="https://groupme.com/..."
                            />
                        </div>

                        <div className="newchapter-field">
                            <label className="newchapter-label">Instagram Link</label>
                            <input
                                className="newchapter-input"
                                value={form.instagram}
                                onChange={(e) => updateField('instagram', e.target.value)}
                                placeholder="https://instagram.com/..."
                            />
                        </div>

                        <div className="newchapter-field newchapter-span2">
                            <label className="newchapter-label">Subscribe / Email List Link</label>
                            <input
                                className="newchapter-input"
                                value={form.subscribeUrl}
                                onChange={(e) => updateField('subscribeUrl', e.target.value)}
                                placeholder="https://soleministries.org/subscribe"
                            />
                        </div>
                    </div>

                    {error ? <div className="newchapter-error">{error}</div> : null}
                    {success ? <div className="newchapter-success">{success}</div> : null}

                    <div className="newchapter-actions">
                        <button type="submit" className="sole-btn sole-btn--primary" disabled={isSaving}>
                            {isSaving ? 'Creating…' : 'Create Chapter'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
